/**
 * Panel de Gestión de Solicitudes de Acceso.
 * Permite a los administradores revisar, aprobar o gestionar las peticiones de nuevos usuarios
 * que desean ingresar a la plataforma, controlando su estado (pendiente, revisado, resuelto).
 */

import React, { useEffect, useState, useCallback, useRef } from "react";
import { Apiurl } from "../../shared/services/apirest";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle";
import AdminLayout from "./AdminLayout";

function AdminAccessRequests() {
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const toastRef = useRef(null);

    /**
     * Muestra notificaciones tipo Toast.
     */
    const showToast = (message, type = "success") => {
        const toastEl = toastRef.current;
        if (toastEl) {
            const bsToast = new bootstrap.Toast(toastEl);
            const toastBody = toastEl.querySelector(".toast-body");
            toastBody.textContent = message;
            toastEl.className = `toast align-items-center border-0 text-bg-${type} shadow-lg`;
            bsToast.show();
        }
    };

    /**
     * Obtiene la lista de solicitudes de acceso desde el backend.
     */
    const fetchRequests = useCallback(() => {
        const token = localStorage.getItem("token");
        if (!token) {
            navigate("/Login");
            return;
        }

        const url = `${Apiurl}users/access-requests/`;
        const config = { headers: { Authorization: `Bearer ${token}` } };

        axios
            .get(url, config)
            .then((res) => {
                setRequests(res.data);
            })
            .catch((err) => {
                console.error("Error fetching access requests:", err);
                showToast("Error al cargar las solicitudes.", "danger");
            })
            .finally(() => setLoading(false));
    }, [navigate]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    /**
     * Actualiza el estado de una solicitud (ej: de 'pendiente' a 'revisado' o 'resuelto').
     */
    const updateStatus = (id, newStatus) => {
        const token = localStorage.getItem("token");
        const url = `${Apiurl}users/access-requests/${id}/`;
        const config = { headers: { Authorization: `Bearer ${token}` } };

        axios
            .patch(url, { status: newStatus }, config)
            .then(() => {
                showToast(`Solicitud marcada como ${newStatus}.`, "success");
                setRequests((prev) =>
                    prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r))
                );
            })
            .catch((err) => {
                console.error("Error updating status:", err);
                showToast("Error al actualizar el estado.", "danger");
            });
    };

    /**
     * Elimina una solicitud de acceso del sistema.
     */
    const deleteRequest = (id) => {
        const token = localStorage.getItem("token");
        const url = `${Apiurl}users/access-requests/${id}/`;
        const config = { headers: { Authorization: `Bearer ${token}` } };

        axios
            .delete(url, config)
            .then(() => {
                showToast("Solicitud eliminada.", "success");
                setRequests((prev) => prev.filter((r) => r.id !== id));
            })
            .catch((err) => {
                console.error("Error deleting request:", err);
                showToast("Error al eliminar la solicitud.", "danger");
            });
    };

    return (
        <AdminLayout
            breadcrumb="Panel de Administración / Solicitudes de Acceso"
            title="Solicitudes de Acceso"
        >
            {/* Contenedor de Notificaciones */}
            <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 1055 }}>
                <div ref={toastRef} className="toast" role="alert" aria-live="assertive" aria-atomic="true">
                    <div className="d-flex">
                        <div className="toast-body fw-semibold"></div>
                        <button type="button" className="btn-close me-2 m-auto" data-bs-dismiss="toast" aria-label="Cerrar"></button>
                    </div>
                </div>
            </div>

            <div className="table-container shadow-sm p-4 bg-white rounded-4">
                <div className="table-responsive">
                    <table className="table admin-table align-middle">
                        <thead>
                            <tr>
                                <th>Nombre Completo</th>
                                <th>Correo Electrónico</th>
                                <th>Motivo de Acceso</th>
                                <th>Tiempo Estimado</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status"></div>
                                        <p className="mt-2 text-muted">Cargando solicitudes...</p>
                                    </td>
                                </tr>
                            ) : requests.length > 0 ? (
                                requests.map((r) => (
                                    <tr key={r.id}>
                                        <td><strong>{r.full_name}</strong></td>
                                        <td>{r.email}</td>
                                        <td style={{ maxWidth: "250px" }}>{r.reason}</td>
                                        <td>{r.estimated_time || 'N/A'}</td>
                                        <td>
                                            <span className={`badge bg-${r.status === 'pendiente' ? 'warning text-dark' : r.status === 'revisado' ? 'info' : 'success'}`}>
                                                {r.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                {r.status === 'pendiente' && (
                                                    <button
                                                        className="btn btn-sm btn-outline-info"
                                                        onClick={() => updateStatus(r.id, 'revisado')}
                                                        title="Marcar como revisado"
                                                    >
                                                        <i className="bi bi-eye"></i>
                                                    </button>
                                                )}
                                                {r.status !== 'resuelto' && (
                                                    <button
                                                        className="btn btn-sm btn-outline-success"
                                                        onClick={() => updateStatus(r.id, 'resuelto')}
                                                        title="Marcar como resuelto"
                                                    >
                                                        <i className="bi bi-check-lg"></i>
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => deleteRequest(r.id)}
                                                    title="Eliminar solicitud"
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-5 text-muted">
                                        No hay solicitudes de acceso registradas.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminAccessRequests;
